import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';

const SAVE_LABELS = {
  saved: '저장됨',
  saving: '저장 중...',
  unsaved: '미저장',
  error: '오류',
};

const SAVE_COLORS = {
  saved: '#4CAF50',
  saving: '#2196F3',
  unsaved: '#FF9800',
  error: '#F44336',
};

const ToolBar = ({ tags, onTagsChange, onDelete, onPin, onDuplicate, onExport, saveStatus, isPinned, allTags }) => {
  const [tagInput, setTagInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Filter suggestions based on input
  const suggestions = useMemo(() => {
    if (!tagInput.trim() || !allTags) return [];
    const query = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (!query) return [];
    return allTags
      .filter(t => t.toLowerCase().includes(query) && !tags.includes(t))
      .slice(0, 8);
  }, [tagInput, allTags, tags]);

  useEffect(() => {
    setShowSuggestions(suggestions.length > 0);
    setSelectedSuggestion(-1);
  }, [suggestions]);

  const addTag = useCallback((raw) => {
    const tag = raw.trim().toLowerCase().replace(/^#/, '');
    if (!tag) return;
    if (tags.includes(tag)) return;
    onTagsChange([...tags, tag]);
    setTagInput('');
    setShowSuggestions(false);
  }, [tags, onTagsChange]);

  const removeTag = useCallback((tag) => {
    onTagsChange(tags.filter(t => t !== tag));
  }, [tags, onTagsChange]);

  const handleKeyDown = useCallback((e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestion(prev => Math.min(prev + 1, suggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestion(prev => Math.max(prev - 1, -1));
        return;
      }
      if (e.key === 'Enter' && selectedSuggestion >= 0) {
        e.preventDefault();
        addTag(suggestions[selectedSuggestion]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }

    if ((e.key === 'Enter' || e.key === ',') && !e.nativeEvent.isComposing) {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  }, [tagInput, tags, addTag, removeTag, showSuggestions, suggestions, selectedSuggestion]);

  return (
    <div className="bear-toolbar">
      {/* Tags */}
      <div className="bear-toolbar-tags" style={{ position: 'relative' }}>
        {tags.map(tag => (
          <span key={tag} className="bear-tag-chip">
            #{tag}
            <button
              className="bear-tag-chip-remove"
              onClick={() => removeTag(tag)}
              title={`#${tag} 삭제`}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="bear-tag-input"
          placeholder={tags.length === 0 ? '태그 추가...' : ''}
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Delay to allow clicking suggestion
            setTimeout(() => {
              if (tagInput) addTag(tagInput);
              setShowSuggestions(false);
            }, 150);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
        />

        {/* Autocomplete dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="bear-tag-autocomplete" ref={suggestionsRef}>
            {suggestions.map((s, i) => (
              <div
                key={s}
                className={`bear-tag-autocomplete-item ${i === selectedSuggestion ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(s);
                }}
              >
                #{s}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bear-toolbar-actions">
        {onPin && (
          <button
            className={`bear-toolbar-btn ${isPinned ? 'pinned' : ''}`}
            onClick={onPin}
            title={isPinned ? '고정 해제' : '상단에 고정'}
          >
            {isPinned ? '📌' : '📍'}
          </button>
        )}
        {onDuplicate && (
          <button className="bear-toolbar-btn" onClick={onDuplicate} title="복제 (Cmd+D)">
            📋
          </button>
        )}
        {onExport && (
          <button className="bear-toolbar-btn" onClick={onExport} title=".md 파일로 내보내기 (Cmd+E)">
            📥
          </button>
        )}
        {onDelete && (
          <button className="bear-toolbar-btn danger" onClick={onDelete} title="삭제">
            🗑
          </button>
        )}
        <span className="bear-toolbar-save">
          <span
            className="bear-toolbar-save-dot"
            style={{ backgroundColor: SAVE_COLORS[saveStatus] || SAVE_COLORS.saved }}
          />
          {SAVE_LABELS[saveStatus] || '저장됨'}
        </span>
      </div>
    </div>
  );
};

export default ToolBar;
